import React from "react";

import { find } from "lodash/fp";

import type { SelectChangeEvent } from "@mui/material";

import SettingsToggle from "@app/analytics/components/SettingsToggle";
import { RecommendedChip } from "@app/analytics/onboarding/components/styles";
import type { User, UserSettings } from "@app/common/types";

import Dropdown from "@rui/Dropdown";
import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import Stack from "@rui/Stack";

import type {
  SelectSetting as ISelectSetting,
  Setting as ISetting,
  ToggleSetting as IToggleSetting,
} from "./settings";

interface CommonSettingProps {
  disabled: boolean;
  isPreview: boolean;
  onChange: (settings: Partial<UserSettings>) => void;
  tooltip?: string;
  user: User;
}

interface SelectSettingProps extends CommonSettingProps {
  setting: ISelectSetting;
}

interface ToggleSettingProps extends CommonSettingProps {
  setting: IToggleSetting;
}

interface EditableSettingProps extends CommonSettingProps {
  setting: ISetting;
}

const SelectSetting = ({
  disabled,
  isPreview,
  onChange,
  setting,
  tooltip,
  user,
}: SelectSettingProps) => {
  const options = setting.options(user);

  const handleChange = (event: SelectChangeEvent) => {
    const settings = setting.getSettingsFromValue(event.target.value, user);
    onChange(settings);
  };

  const selectedItem = setting.getOptionFromSettings(user, options);
  const defaultValue = isPreview
    ? find({ default: true }, options)?.value
    : undefined;

  return (
    <Dropdown
      name={setting.name}
      disabled={disabled}
      tooltip={tooltip}
      onChange={handleChange}
      selectedItem={selectedItem}
      defaultValue={defaultValue}
      renderDefaultLabel={() => <RecommendedChip />}
      wrapperSx={{
        "& fieldset": { borderColor: "shades.neutral.100" },
      }}
      fullWidth
    >
      {options.map((option) => (
        <DropdownMenuItem
          key={option.value}
          label={option.label}
          value={option.value}
          isDefault={option.default}
          renderDefaultLabel={() => <RecommendedChip />}
        />
      ))}
    </Dropdown>
  );
};

const ToggleSetting = ({
  disabled,
  onChange,
  setting,
  tooltip,
  user,
}: ToggleSettingProps) => {
  const handleChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const value = checked ? "true" : "false";
    onChange(setting.getSettingsFromValue(value, user));
  };

  const selectedValue = setting.getOptionFromSettings(user);

  return (
    <Stack variant="right">
      <SettingsToggle
        label={selectedValue === "true" ? "ON" : "OFF"}
        disabled={disabled}
        disabledTooltip={tooltip}
        checked={selectedValue === "true"}
        onChange={handleChange}
      />
    </Stack>
  );
};

const EditableSetting = ({
  onChange,
  setting,
  ...props
}: EditableSettingProps) => {
  switch (setting.type) {
    case "toggle":
      return (
        <ToggleSetting
          {...props}
          setting={setting as IToggleSetting}
          onChange={onChange}
        />
      );
    case "select":
      return (
        <SelectSetting
          {...props}
          setting={setting as ISelectSetting}
          onChange={onChange}
        />
      );
    default:
      return null;
  }
};

export default EditableSetting;
